/**
 * Phase 5 batch 2 Slice 5 — Cloud Storage adapter for `document_upload`
 * fields.
 *
 * Form-engine answers of `type: 'document_upload'` carry a
 * `DocumentUploadValue.uploadedFileIds: string[]` — the binaries
 * themselves live in Cloud Storage. This adapter owns the binary
 * lifecycle (upload, download URL minting, delete, list) and is
 * intentionally engine-agnostic: the engine still sees a string-array
 * answer; the storage adapter is composed on top by the applicant-web
 * UI in Phase 5 batch 3 (Slice 8).
 *
 * ## Storage path scheme
 *
 *   users/{uid}/applications/{applicationId}/{fieldId}/{fileId}-{originalName}
 *
 * Why per-user-isolated, *not* the legacy
 * `applications/{applicationId}/{fieldId}/{fileId}` layout:
 *
 * - **Rules cost**: a path-prefix check (`request.auth.uid == path[1]`)
 *   is a pure string match. The legacy layout requires a
 *   `firestore.get(/databases/(default)/documents/applications/$(appId))`
 *   inside the storage rule on every upload, which is a billed
 *   cross-service read AND adds latency. Per-user paths make the rule
 *   O(1) string comparison.
 * - **Defense in depth**: even if the Firestore `applications/{appId}`
 *   doc is corrupted or its `ownerUserId` is somehow rewritten (the
 *   rule blocks this, but defense in depth is cheap), the storage
 *   layout itself encodes ownership in the path.
 * - **Listing**: a per-user prefix lets us issue `listAll(users/{uid})`
 *   safely for "show me everything I uploaded across all my
 *   applications" — useful for an account-deletion flow.
 *
 * Trade-off: the path no longer matches the Firestore document layout
 * 1:1. We accept that in exchange for the rule simplicity. The
 * Firestore `applications/{appId}/documents/{documentId}` metadata
 * subcollection (already created in Slice 1's rules) records the
 * `storagePath` so the cross-reference is preserved.
 *
 * ## MIME-type validation
 *
 * Two layers:
 *
 * 1. **Client-side via `acceptMimeTypes`**: caller supplies the
 *    catalog's `documentSpec.acceptedFormats` (or an explicit
 *    allowlist). The adapter rejects mismatches *before* uploading
 *    so the user sees a fast error.
 * 2. **Storage rules**: the production allowlist (PDF, JPEG, PNG,
 *    HEIC, HEIF, WebP, DOC, DOCX) is enforced server-side regardless
 *    of caller arguments.
 *
 * Source-cited Firebase APIs (verified against
 * `node_modules/@firebase/storage/dist/storage-public.d.ts`):
 *   - `ref(storage, path)` → StorageReference
 *   - `uploadBytes(ref, data, metadata?)` → Promise<UploadResult>
 *   - `getDownloadURL(ref)` → Promise<string>
 *   - `deleteObject(ref)` → Promise<void>
 *   - `listAll(ref)` → Promise<ListResult>
 *
 * https://firebase.google.com/docs/storage/web/upload-files
 * https://firebase.google.com/docs/storage/web/download-files
 * https://firebase.google.com/docs/storage/web/delete-files
 * https://firebase.google.com/docs/storage/web/list-files
 */

import {
  type FirebaseStorage,
  type StorageReference,
  deleteObject,
  getDownloadURL,
  getMetadata,
  listAll,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';

/** Caller-supplied identity. The adapter never reads Firebase Auth
 *  directly; the applicant-web app injects the current `uid`. */
export interface StorageIdentity {
  uid: string;
}

/** Returned by `uploadDocument`. */
export interface UploadedDocument {
  /** Stable id (the random component of the path's filename). */
  fileId: string;
  /** Bucket-relative full path. Persist this on the metadata doc. */
  storagePath: string;
  /** A signed, time-limited URL the UI can render an `<img>` from. */
  downloadUrl: string;
  /** Bytes, as reported by the server. */
  size: number;
  /** Server-recorded content type. */
  contentType: string;
  /** ISO-8601 server timestamp. */
  uploadedAt: string;
}

/** Optional behavior knobs for `uploadDocument`. */
export interface UploadOptions {
  /**
   * Allowlist of MIME types this call accepts. Falls back to the
   * production-wide allowlist (see `DEFAULT_ACCEPT_MIME_TYPES`)
   * when undefined. Pass the catalog's
   * `documentSpec.acceptedFormats` to scope per-field.
   */
  acceptMimeTypes?: readonly string[];
  /**
   * Hard upper bound (bytes). Server rules already cap at 10 MiB;
   * this argument lets a caller tighten further (e.g., a 2 MiB cap
   * on a passport photo). Defaults to the server cap.
   */
  maxSizeBytes?: number;
  /**
   * Optional explicit fileId. The default is a crypto-random
   * 16-char hex string. Tests pin this for determinism.
   */
  fileId?: string;
}

/**
 * Production-wide MIME-type allowlist. Mirrors `storage.rules`.
 * Keep these in lockstep — a divergence is a security gap.
 */
export const DEFAULT_ACCEPT_MIME_TYPES = Object.freeze([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const);

/** 10 MiB — must match `storage.rules`. */
export const DEFAULT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

export class DocumentUploadRejectedError extends Error {
  readonly code:
    | 'mime-not-allowed'
    | 'size-exceeded'
    | 'invalid-file';
  readonly details: Record<string, unknown>;

  constructor(
    code: DocumentUploadRejectedError['code'],
    message: string,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'DocumentUploadRejectedError';
    this.code = code;
    this.details = details;
  }
}

export interface StorageClient {
  /**
   * Uploads `file` to the per-user-isolated path scheme. Validates
   * MIME type and size before issuing the network call. The
   * returned `UploadedDocument` is the value to pin onto the
   * Firestore `applications/{appId}/documents/{fileId}` metadata
   * doc; the adapter does not write that metadata itself.
   */
  uploadDocument: (
    identity: StorageIdentity,
    applicationId: string,
    fieldId: string,
    file: File | Blob,
    options?: UploadOptions,
  ) => Promise<UploadedDocument>;
  /** Mints a signed URL for an already-uploaded path. */
  getDownloadUrl: (storagePath: string) => Promise<string>;
  /** Deletes the object. The corresponding metadata doc must be
   *  deleted by the caller; the adapter is single-responsibility. */
  deleteDocument: (storagePath: string) => Promise<void>;
  /**
   * Lists all currently-uploaded documents under
   *   users/{uid}/applications/{applicationId}/{fieldId}/
   * Useful for rendering "you've uploaded N files" affordances.
   */
  listDocuments: (
    identity: StorageIdentity,
    applicationId: string,
    fieldId: string,
  ) => Promise<readonly UploadedDocument[]>;
}

/** Generates a random fileId. Crypto-strong, hex-encoded, 16 chars
 *  (8 bytes — collision probability negligible in practice). */
const generateFileId = (): string => {
  const bytes = new Uint8Array(8);
  // Use the modern Web Crypto API. Vitest's `node` env exposes this
  // on `globalThis.crypto` since Node 20.
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/** Strips path-traversal and slashes from the original filename so
 *  the storage path stays well-formed. */
const sanitizeFilename = (raw: string): string => {
  // Drop any directory components, control chars, and slashes.
  // Keep alphanumerics, dots, dashes, underscores.
  const base = raw.split(/[\\/]/).pop() ?? raw;
  const cleaned = base.replace(/[^A-Za-z0-9._-]/g, '_');
  // Avoid a leading dot that would yield a hidden file.
  return cleaned.replace(/^\.+/, '_');
};

/**
 * Encodes `fieldId` for path safety. Field ids are
 * dot-separated (e.g., `beneficiary.name.legal.first`) — Storage
 * paths support dots, but for clarity (and to keep the listAll
 * prefix predictable) we replace dots with `__` so the field
 * occupies exactly one path segment.
 */
const encodeFieldIdSegment = (fieldId: string): string =>
  fieldId.replace(/\./g, '__');

/**
 * Builds the canonical per-file path. Exported so tests can pin the
 * scheme without going through `uploadDocument`.
 */
/**
 * Separator between the fileId prefix and the original filename in
 * the path's leaf segment. Chosen to be a sequence that the
 * crypto-random hex fileId can never contain AND that
 * `sanitizeFilename` will collapse if present in user input — so the
 * separator is reliably the first `__` in the leaf.
 */
const FILEID_FILENAME_SEPARATOR = '__';

export const buildStoragePath = (
  identity: StorageIdentity,
  applicationId: string,
  fieldId: string,
  fileId: string,
  fileName: string,
): string =>
  `users/${identity.uid}/applications/${applicationId}/` +
  `${encodeFieldIdSegment(fieldId)}/${fileId}${FILEID_FILENAME_SEPARATOR}` +
  `${sanitizeFilename(fileName)}`;

/**
 * Parses a storage path back into its constituents. Returns `null`
 * if the path doesn't match the canonical scheme. Used by the
 * UI when rendering a list of uploads — the metadata doc is the
 * authoritative source, but the path itself is an audit trail.
 */
export const parseStoragePath = (
  path: string,
): {
  uid: string;
  applicationId: string;
  fieldId: string;
  fileId: string;
  fileName: string;
} | null => {
  // Canonical layout has six `/`-separated parts:
  //   ["users", uid, "applications", appId, fieldSeg, leaf]
  const parts = path.split('/');
  if (parts.length !== 6) return null;
  if (parts[0] !== 'users' || parts[2] !== 'applications') return null;
  const leaf = parts[5]!;
  const sepIdx = leaf.indexOf(FILEID_FILENAME_SEPARATOR);
  if (sepIdx <= 0 || sepIdx >= leaf.length - FILEID_FILENAME_SEPARATOR.length) {
    return null;
  }
  return {
    uid: parts[1]!,
    applicationId: parts[3]!,
    // Reverse `encodeFieldIdSegment` (`.` -> `__`).
    fieldId: parts[4]!.replace(/__/g, '.'),
    fileId: leaf.slice(0, sepIdx),
    fileName: leaf.slice(sepIdx + FILEID_FILENAME_SEPARATOR.length),
  };
};

const validateAccept = (
  contentType: string,
  accept: readonly string[],
): void => {
  if (!accept.includes(contentType)) {
    throw new DocumentUploadRejectedError(
      'mime-not-allowed',
      `Content-type '${contentType}' is not in the allowlist [${accept.join(', ')}].`,
      { contentType, accept: [...accept] },
    );
  }
};

const validateSize = (size: number, max: number): void => {
  if (!Number.isFinite(size) || size <= 0) {
    throw new DocumentUploadRejectedError(
      'invalid-file',
      `File size ${size} is invalid (must be > 0).`,
      { size },
    );
  }
  if (size > max) {
    throw new DocumentUploadRejectedError(
      'size-exceeded',
      `File size ${size} bytes exceeds the cap of ${max} bytes.`,
      { size, max },
    );
  }
};

/**
 * Builds a `StorageClient` against the supplied `FirebaseStorage`
 * instance. The client does not own SDK initialization —
 * `getStorage(app)` is the caller's responsibility, mirroring the
 * persistence-adapter contract.
 */
export const createStorageClient = (
  storage: FirebaseStorage,
): StorageClient => {
  const refFor = (path: string): StorageReference => storageRef(storage, path);

  const uploadDocument: StorageClient['uploadDocument'] = async (
    identity,
    applicationId,
    fieldId,
    file,
    options,
  ) => {
    const accept = options?.acceptMimeTypes ?? DEFAULT_ACCEPT_MIME_TYPES;
    const maxSize = options?.maxSizeBytes ?? DEFAULT_MAX_SIZE_BYTES;
    const contentType = file.type || 'application/octet-stream';
    validateAccept(contentType, accept);
    validateSize(file.size, maxSize);

    const fileId = options?.fileId ?? generateFileId();
    // `File` exposes `.name`; `Blob` does not. Fall back to fileId.
    const originalName = (file as File).name ?? `${fileId}.bin`;
    const path = buildStoragePath(
      identity,
      applicationId,
      fieldId,
      fileId,
      originalName,
    );
    const target = refFor(path);

    const result = await uploadBytes(target, file, { contentType });
    const meta = result.metadata;
    const downloadUrl = await getDownloadURL(target);

    return {
      fileId,
      storagePath: path,
      downloadUrl,
      // `meta.size` is the server-truth byte count.
      size: meta.size ?? file.size,
      contentType: meta.contentType ?? contentType,
      uploadedAt: meta.timeCreated ?? new Date().toISOString(),
    };
  };

  const getDownloadUrl: StorageClient['getDownloadUrl'] = (path) =>
    getDownloadURL(refFor(path));

  const deleteDocument: StorageClient['deleteDocument'] = (path) =>
    deleteObject(refFor(path));

  const listDocuments: StorageClient['listDocuments'] = async (
    identity,
    applicationId,
    fieldId,
  ) => {
    const prefix =
      `users/${identity.uid}/applications/${applicationId}/` +
      `${encodeFieldIdSegment(fieldId)}`;
    const result = await listAll(refFor(prefix));
    const docs = await Promise.all(
      result.items.map(async (item): Promise<UploadedDocument> => {
        const [meta, url] = await Promise.all([
          getMetadata(item),
          getDownloadURL(item),
        ]);
        const parsed = parseStoragePath(item.fullPath);
        const fileId =
          parsed?.fileId ??
          // Defensive fallback: take the leading dash-separated chunk.
          item.name.split('-')[0] ??
          item.name;
        return {
          fileId,
          storagePath: item.fullPath,
          downloadUrl: url,
          size: meta.size ?? 0,
          contentType: meta.contentType ?? 'application/octet-stream',
          uploadedAt: meta.timeCreated ?? new Date().toISOString(),
        };
      }),
    );
    return docs;
  };

  return {
    uploadDocument,
    getDownloadUrl,
    deleteDocument,
    listDocuments,
  };
};
